import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "./Icon";
import { supabase } from "../supabaseClient";

const AdminBreadcrumb = ({ menuHidden, setMenuHidden }) => {
  const location = useLocation();
  const [eventNames, setEventNames] = useState({});
  const [dealNames, setDealNames] = useState({});
  const [tokenNames, setTokenNames] = useState({});

  // Map of admin routes to friendly names
  const routeMap = {
    events: "Events",
    lifestyle: "Lifestyle deals",
    venues: "Venues",
    cities: "Cities",
    suggestions: "Suggestions",
    "lifestyle-categories": "Lifestyle categories",
    tokens: "Tokens",
    "community-chats": "Community chats",
    "trending-events": "Trending events",
    "spin-win-events": "Spin & Win events",
    internships: "Internships",
  };

  // Fetch names for IDs when component mounts
  useEffect(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const eventIds = [];
    const dealIds = [];
    const tokenIds = [];

    // Find segments that are likely IDs (numeric values)
    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i];
      const prevSegment = pathSegments[i - 1];

      // Check if this segment is an ID based on the previous segment
      if (prevSegment === "event" && /^\d+$/.test(segment)) {
        eventIds.push(segment);
      } else if (prevSegment === "deal" && /^\d+$/.test(segment)) {
        dealIds.push(segment);
      } else if (prevSegment === "token" && /^\d+$/.test(segment)) {
        tokenIds.push(segment);
      }
    }

    // Fetch event names
    if (eventIds.length > 0) {
      supabase
        .from("Events")
        .select("id, name")
        .in("id", eventIds)
        .then((response) => {
          if (response.data) {
            const names = {};
            response.data.forEach((event) => {
              names[event.id] = event.name;
            });
            setEventNames(names);
          }
        });
    }

    // Fetch deal names
    if (dealIds.length > 0) {
      supabase
        .from("Deals")
        .select("id, name")
        .in("id", dealIds)
        .then((response) => {
          if (response.data) {
            const names = {};
            response.data.forEach((deal) => {
              names[deal.id] = deal.name;
            });
            setDealNames(names);
          }
        });
    }

    // Fetch token names
    if (tokenIds.length > 0) {
      supabase
        .from("Tokens")
        .select("id, name")
        .in("id", tokenIds)
        .then((response) => {
          if (response.data) {
            const names = {};
            response.data.forEach((token) => {
              names[token.id] = token.name;
            });
            setTokenNames(names);
          }
        });
    }
  }, [location.pathname]);

  const generateBreadcrumbs = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const breadcrumbs = [{ name: "Home", path: "/", isLast: false }];

    if (pathSegments.length > 0) {
      // Skip the first segment if it's "admin"
      const relevantSegments =
        pathSegments[0] === "admin" ? pathSegments.slice(1) : pathSegments;

      relevantSegments.forEach((segment, index) => {
        const isLast = index === relevantSegments.length - 1;
        let friendlyName = routeMap[segment] || segment;
        const prevSegment = relevantSegments[index - 1];

        // Handle specific ID segments with fetched names
        if (/^\d+$/.test(segment)) {
          if (prevSegment === "event" && eventNames[segment]) {
            friendlyName = eventNames[segment];
          } else if (prevSegment === "deal" && dealNames[segment]) {
            friendlyName = dealNames[segment];
          } else if (prevSegment === "token" && tokenNames[segment]) {
            friendlyName = tokenNames[segment];
          } else {
            friendlyName = `ID: ${segment}`;
          }
        }
        // Handle dynamic segments (cities, categories, etc.)
        else if (!routeMap[segment]) {
          // Capitalize first letter and replace dashes with spaces
          friendlyName =
            segment.charAt(0).toUpperCase() +
            segment.slice(1).replace(/-/g, " ");
        }

        breadcrumbs.push({
          name: friendlyName,
          path: `/${pathSegments.slice(0, index + 2).join("/")}`,
          isLast,
        });
      });
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  return (
    <div className="flex items-center gap-[8px] text-[14px]">
      <button
        onClick={() => setMenuHidden(!menuHidden)}
        className="hover:bg-[#D0ADFB20] mr-[12px] p-2 rounded-lg transition-colors"
        title="Open sidebar"
      >
        <Icon icon="mynaui:sidebar" className="w-[18px] h-[18px] text-white" />
      </button>
      {breadcrumbs.map((breadcrumb, index) => (
        <React.Fragment key={breadcrumb.path}>
          {breadcrumb.isLast ? (
            <span className="font-medium text-white">{breadcrumb.name}</span>
          ) : (
            <Link
              to={breadcrumb.path}
              className="font-normal text-[14px] text-neutral-300 tracking-[0px]"
            >
              {breadcrumb.name}
            </Link>
          )}
          {!breadcrumb.isLast && (
            <Icon
              icon="heroicons-solid:chevron-right"
              className="w-[14px] h-[14px] text-neutral-300"
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default AdminBreadcrumb;
